// Frontend/src/lib/notificationStream.ts
//
// One connection per browser tab for the whole app, not one per component.
// NotificationBell mounts in all three layouts and the dedicated
// /resident/notifications page wants the same live feed -- connect() is
// idempotent so every mount point can call it without opening duplicates.
//
// Deliberately fetch(), not EventSource: EventSource cannot set a request
// header, and every other call in this app authenticates the same way
// everything else does -- an Authorization: Bearer header from the same
// token store api/client.ts already manages. A query-string token would
// work with native EventSource, but it puts a bearer credential in server
// access logs and browser history for no reason when fetch's ReadableStream
// reads an SSE-formatted body just as well.
//
// Backend contract: backend/notifications/stream.py. It closes every stream
// on a schedule (event: cycle-end) well under gunicorn's and nginx's own
// timeouts, so a "disconnect" here is almost always that scheduled handoff,
// not a failure -- see sseReconnectPolicy.ts for how those two cases are
// told apart and handled differently.

import { getAccessToken, refreshAccessToken } from "@/api/client";
import { parseSseFrame } from "./sseFrame";
import { SseReconnectPolicy, type SseStatus } from "./sseReconnectPolicy";

export type StreamNotification = {
  id: string;
  kind: string;
  kind_display: string;
  title: string;
  body: string;
  is_read: boolean;
  read_at: string | null;
  target_type: string | null;
  target_object_id: string | null;
  target_building_id?: string | null;
  created_at: string;
};

type EventListener = (notification: StreamNotification) => void;
type StatusListener = (status: SseStatus) => void;

const STREAM_URL = "/api/v1/notifications/stream/";

class NotificationStreamClient {
  private policy = new SseReconnectPolicy();
  private eventListeners = new Set<EventListener>();
  private statusListeners = new Set<StatusListener>();
  private abortController: AbortController | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private wakeReconnectWait: (() => void) | null = null;
  private lastEventId: string | null = null;
  private stopped = true;
  private runLoopActive = false;

  subscribe(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.policy.status);
    return () => this.statusListeners.delete(listener);
  }

  get status(): SseStatus {
    return this.policy.status;
  }

  connect(): void {
    if (!this.stopped) return; // already connecting/connected
    this.stopped = false;
    if (!this.runLoopActive) {
      this.runLoopActive = true;
      void this.runLoop();
    }
  }

  disconnect(): void {
    this.stopped = true;
    this.lastEventId = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // A cleared timeout's callback never fires, so without this the run loop's
    // `await new Promise(...)` below would hang forever mid-backoff: the
    // resolve it's waiting on would simply never come, leaving runLoopActive
    // stuck true and a later connect() unable to start a fresh loop.
    this.wakeReconnectWait?.();
    this.wakeReconnectWait = null;
    this.abortController?.abort();
    this.abortController = null;
  }

  private setStatus(status: SseStatus) {
    this.statusListeners.forEach((listener) => listener(status));
  }

  private async runLoop() {
    while (!this.stopped) {
      const outcome = await this.openOnce();
      if (this.stopped) break;

      let delay: number;
      if (outcome === "clean") {
        delay = this.policy.recordCleanClose();
      } else {
        delay = this.policy.recordFailure();
      }
      this.setStatus(this.policy.status);

      await new Promise<void>((resolve) => {
        this.wakeReconnectWait = resolve;
        this.reconnectTimer = setTimeout(resolve, delay);
      });
      this.wakeReconnectWait = null;
      if (this.stopped) break;
      if (this.policy.status === "degraded") {
        this.policy.retryFromDegraded();
      }
    }
    this.runLoopActive = false;
  }

  /** One connection attempt, start to finish. Returns how it ended. */
  private async openOnce(): Promise<"clean" | "error"> {
    const token = getAccessToken() || sessionStorage.getItem("access_token");
    if (!token) return "error"; // nothing to authenticate with yet

    this.abortController = new AbortController();
    this.setStatus("connecting");

    try {
      const response = await this.fetchWithAuthRetry(token);
      if (!response || !response.body) return "error";
      if (!response.ok) {
        // Never read, so the underlying connection would otherwise sit open
        // until GC -- irrelevant for one failed attempt, not for a repeated
        // backoff loop holding a connection per try.
        void response.body.cancel();
        return "error";
      }

      this.policy.recordSuccess();
      this.setStatus("open");

      return await this.readFrames(response.body);
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return "clean"; // disconnect() called mid-read
      return "error";
    }
  }

  private async fetchWithAuthRetry(token: string): Promise<Response | null> {
    const doFetch = (bearer: string) =>
      fetch(STREAM_URL, {
        headers: {
          Authorization: `Bearer ${bearer}`,
          Accept: "text/event-stream",
          ...(this.lastEventId ? { "Last-Event-ID": this.lastEventId } : {}),
        },
        signal: this.abortController!.signal,
      });

    let response = await doFetch(token);
    if (response.status === 401) {
      try {
        const fresh = await refreshAccessToken();
        response = await doFetch(fresh);
      } catch {
        return null; // refresh failed -- likely a real logout, not a transient blip
      }
    }
    return response;
  }

  /**
   * Reads and dispatches frames until the body ends. Returns "clean" only if
   * the last frame we saw was the server's own scheduled cycle-end -- any
   * other way the stream ends (network drop, server error mid-stream) is
   * "error", even though no exception was thrown.
   */
  private async readFrames(body: ReadableStream<Uint8Array>): Promise<"clean" | "error"> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let endedClean = false;

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          if (this.handleFrame(frame)) endedClean = true;
          boundary = buffer.indexOf("\n\n");
        }
      }
    } finally {
      reader.releaseLock();
    }

    return endedClean ? "clean" : "error";
  }

  /** Returns true if this frame was the server's cycle-end marker. */
  private handleFrame(frame: string): boolean {
    const parsed = parseSseFrame(frame);
    if (parsed.type !== "heartbeat" && parsed.id) this.lastEventId = parsed.id;

    if (parsed.type === "cycle-end") return true;
    if (parsed.type === "notification") {
      this.eventListeners.forEach((listener) => listener(parsed.data));
    }
    return false;
  }
}

export const notificationStream = new NotificationStreamClient();

if (typeof window !== "undefined") {
  window.addEventListener("urbe360:logout", () => notificationStream.disconnect());
}
